import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * The billing settings of this edition: the due days that an issued invoice gets when it has no
 * due date, and the number format of the invoices and the credit notes.
 */
const numberFormatSchema = z.object({
  prefix: z.string(),
  padding: z.number(),
  /** The counter of the next document: `42`. */
  nextValue: z.number(),
  /** The number that the next document gets: `INV-0042`. */
  next: z.string(),
})

const billingSettingsSchema = z.object({
  invoiceDueDays: z.number(),
  invoiceNumber: numberFormatSchema,
  creditNoteNumber: numberFormatSchema,
})

export type BillingSettings = z.infer<typeof billingSettingsSchema>

const base = '/v1/billing/admin/settings'

export function fetchBillingSettings(signal?: AbortSignal) {
  return api.get(base, { schema: billingSettingsSchema, ...(signal ? { signal } : {}) })
}

export type BillingSettingsBody = {
  invoiceDueDays: number
  invoiceNumber: { prefix: string; padding: number }
  creditNoteNumber: { prefix: string; padding: number }
}

export function saveBillingSettings(body: BillingSettingsBody) {
  return api.put(base, { schema: billingSettingsSchema, body })
}

/** The limits. The API checks them too. */
export const DUE_DAYS = { min: 1, max: 365 } as const
export const PADDING = { min: 1, max: 10 } as const
export const PREFIX_PATTERN = /^[A-Za-z0-9][A-Za-z0-9./-]{0,19}$/

/** The form's values, as the inputs hold them. */
export type SettingsDraft = {
  invoiceDueDays: string
  invoicePrefix: string
  invoicePadding: string
  creditNotePrefix: string
  creditNotePadding: string
}

export function settingsDraftOf(settings: BillingSettings): SettingsDraft {
  return {
    invoiceDueDays: String(settings.invoiceDueDays),
    invoicePrefix: settings.invoiceNumber.prefix,
    invoicePadding: String(settings.invoiceNumber.padding),
    creditNotePrefix: settings.creditNoteNumber.prefix,
    creditNotePadding: String(settings.creditNoteNumber.padding),
  }
}

/** A whole number from `min` to `max`, or null. */
function wholeIn(value: string, range: { min: number; max: number }): number | null {
  if (!/^\d+$/.test(value.trim())) return null
  const n = Number(value)
  return n >= range.min && n <= range.max ? n : null
}

/** What is wrong with the draft, by field. Empty when the draft can be saved. */
export function settingsIssues(draft: SettingsDraft): Partial<Record<keyof SettingsDraft, string>> {
  const issues: Partial<Record<keyof SettingsDraft, string>> = {}
  if (wholeIn(draft.invoiceDueDays, DUE_DAYS) === null) {
    issues.invoiceDueDays = `Type a whole number from ${DUE_DAYS.min} to ${DUE_DAYS.max}.`
  }
  for (const [prefix, padding] of [
    ['invoicePrefix', 'invoicePadding'],
    ['creditNotePrefix', 'creditNotePadding'],
  ] as const) {
    if (!PREFIX_PATTERN.test(draft[prefix].trim())) {
      issues[prefix] =
        'Use 1 to 20 letters, digits, "-", "/" or ".". Start with a letter or a digit.'
    }
    if (wholeIn(draft[padding], PADDING) === null) {
      issues[padding] = `Type a whole number from ${PADDING.min} to ${PADDING.max}.`
    }
  }
  if (
    issues.creditNotePrefix === undefined &&
    draft.invoicePrefix.trim().toUpperCase() === draft.creditNotePrefix.trim().toUpperCase()
  ) {
    issues.creditNotePrefix = 'Use a prefix that is different from the invoice prefix.'
  }
  return issues
}

/** The body of the save. Call it only when `settingsIssues` is empty. */
export function settingsBodyOf(draft: SettingsDraft): BillingSettingsBody {
  return {
    invoiceDueDays: Number(draft.invoiceDueDays),
    invoiceNumber: { prefix: draft.invoicePrefix.trim(), padding: Number(draft.invoicePadding) },
    creditNoteNumber: {
      prefix: draft.creditNotePrefix.trim(),
      padding: Number(draft.creditNotePadding),
    },
  }
}

/**
 * The number that the next document gets with this prefix and padding, as the API formats it:
 * `INV` + `42` + `4` → `INV-0042`. Null while the prefix or the padding is not valid.
 */
export function previewNumber(prefix: string, padding: string, nextValue: number): string | null {
  const width = wholeIn(padding, PADDING)
  if (width === null || !PREFIX_PATTERN.test(prefix.trim())) return null
  return `${prefix.trim()}-${String(nextValue).padStart(width, '0')}`
}
